import React, { useEffect, useState, forwardRef } from "react";
import { Editor } from "@tinymce/tinymce-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material"; // Material-UI for modal

const TinyMCE = forwardRef(
  ({ existingContent, onContentChange, onLogout }, ref) => {
    const [content, setContent] = useState("");
    const [signature, setSignature] = useState(""); // User's signature
    const [openDialog, setOpenDialog] = useState(false); // Control modal visibility

    // Load saved signature from local storage on mount
    useEffect(() => {
      const savedSignature = localStorage.getItem("userSignature");
      if (savedSignature) setSignature(savedSignature);
    }, []);

    useEffect(() => {
      if(existingContent != '' && existingContent != content)
        setContent(existingContent);
    }, [existingContent]);

    // Update editor content
    const handleEditorChange = (newContent) => {
      setContent(newContent);
      onContentChange(newContent);
    };

    // Save the signature
    const handleSaveSignature = () => {
      localStorage.setItem("userSignature", signature);
      setOpenDialog(false);
    };

    // Clear the saved signature
    const handleClearSignature = () => {
      localStorage.removeItem("userSignature");
      setSignature("");
      setOpenDialog(false);
    };

    // Convert new lines to HTML <br> and apply styles
    const formatSignatureForEditor = (text) => {
      const formattedText = text
        .replace(/\n/g, "<br>") // Preserve line breaks
        .replace(
          /^(.+?)<br>/,
          '<strong style="font-size:16px; color:#2c3e50;">$1</strong><br>' // Bold first line (Name)
        )
        .replace(
          /(Website: )(.+)/,
          '$1<a href="$2" target="_blank" style="color:#007bff; text-decoration:none;">$2</a>' // Style website link
        );
      return `<div style="font-family:Arial, sans-serif; font-size:14px; color:#333;">${formattedText}</div>`;
    };

    return (
      <>
        <Editor
          ref={ref}
          apiKey="e2k468m7kvtbl17pcmb9zpug9zajxqvl05b1f3nkvzmgmgr1"
          value={content}
          onEditorChange={handleEditorChange}
          init={{
            plugins:
              "anchor autolink charmap codesample emoticons image link lists media searchreplace table visualblocks wordcount",
            toolbar:
              "undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | link image media table | align lineheight | numlist bullist indent outdent | insertSignature | emoticons charmap |  removeformat",
            font_size_formats: "8pt 10pt 11pt 12pt 14pt 18pt 24pt 36pt",
            content_style: 'body { font-size: 11pt; }',
            font_size_default_groups: '11pt',
            font_size_input_default_value: '11pt',
          
            height: 500,
            convert_urls: false,
            relative_urls: false,
            remove_script_host: false,
            setup: (editor) => {
              // Custom button for inserting a signature
              editor.ui.registry.addButton("insertSignature", {
                text: "Insert Signature",
                icon: "edit-signature",
                tooltip: "Insert or Edit Your Signature",
                onAction: () => {
                  setOpenDialog(true); // Open signature modal
                },
              });
            },
          }}
        />

        {/* Signature Input Modal */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Set Your Signature</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Enter your signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              multiline // Allow multiple lines
              rows={8} // Bigger input area
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleClearSignature} color="secondary">
              Clear
            </Button>
            <Button
              onClick={() => {
                handleSaveSignature();
                ref.current.editor.insertContent(
                  formatSignatureForEditor(signature)
                ); // Insert formatted signature
              }}
              color="primary"
              variant="contained"
            >
              Save & Insert
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default TinyMCE;
